import { useRef } from "react"
import { createFileRoute, Outlet, Link } from "@tanstack/react-router"
import { useAtomValue, useSetAtom } from "jotai"
import { Upload, Search, Workflow } from "lucide-react"
import { AixContentHeader, Button, toast } from "@aix/ui"
import { api } from "~/utils/api"
import AppLayout from "~/layouts/AppLayout"
import { MediaSpaceBreadcrumb } from "~/components/MediaSpace/MediaSpaceBreadcrumb"
import { FolderTree } from "~/components/MediaSpace/FolderTree"
import { UploadDropzone } from "~/components/MediaSpace/UploadDropzone"
import { ViewModeSwitcher } from "~/components/MediaSpace/ViewModeSwitcher"
import { AppearanceControls } from "~/components/MediaSpace/AppearanceControls"
import { SortByDropdown } from "~/components/MediaSpace/SortByDropdown"
import { SearchOverlay } from "~/components/MediaSpace/SearchOverlay"
import { selectedFolderIdAtom, hideFolderSidebarAtom, searchOverlayOpenAtom } from "~/context/mediaSpaceAtoms"
import { useAuthSession } from "~/hooks/useAuthSessionVite"

function MediaSpaceWrapper() {
  return (
    <AppLayout>
      <MediaSpacePage />
    </AppLayout>
  )
}

export const Route = createFileRoute("/media-space")({
  component: MediaSpaceWrapper,
})

function MediaSpacePage() {
  const selectedFolderId = useAtomValue(selectedFolderIdAtom)
  const hideSidebar = useAtomValue(hideFolderSidebarAtom)
  const setSearchOpen = useSetAtom(searchOverlayOpenAtom)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { session } = useAuthSession()

  // Admin gate — only admin users can access media space
  const userRole = (session?.user as any)?.role
  const isAdmin = userRole === "admin" || (session?.user as any)?.internalMisc?.isAdmin === true
  // For dev environment, allow all authenticated users
  const isDev = typeof window !== "undefined" && window.location.hostname === "localhost"

  if (!isDev && !isAdmin && session) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <h2 className="text-lg font-semibold mb-2">Media Space</h2>
          <p className="text-sm text-muted-foreground">This feature is coming soon. Stay tuned!</p>
        </div>
      </div>
    )
  }

  return (
    <>
      {!hideSidebar && (
        <AixContentHeader className="flex-col w-full border-b">
          <div className="flex items-center justify-between w-full">
            <MediaSpaceBreadcrumb />
            <div className="flex items-center gap-2">
              {/* Workflows */}
              <Link to="/media-space/workflows" search={{ tab: "workflows", wf: undefined, exec: undefined }}>
                <Button variant="outline" size="sm" className="gap-1.5">
                  <Workflow size={14} />
                  Workflows
                </Button>
              </Link>

              {/* Search */}
              <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setSearchOpen(true)}>
                <Search size={14} />
                Search
              </Button>

              {/* Sort */}
              <SortByDropdown />

              {/* Appearance */}
              <AppearanceControls />

              {/* Upload */}
              <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}>
                <Upload size={14} className="mr-1.5" />
                Upload
              </Button>

              {/* View mode */}
              <ViewModeSwitcher />
            </div>
          </div>
        </AixContentHeader>
      )}

      <div className="flex flex-1 overflow-hidden min-h-0">
        {!hideSidebar && (
          <div className="w-60 border-r flex-shrink-0 overflow-y-auto">
            <FolderTree />
          </div>
        )}

        {hideSidebar ? (
          <div className="flex-1 overflow-hidden">
            <Outlet />
          </div>
        ) : (
          <UploadDropzone folderId={selectedFolderId}>
            <div className="flex-1 overflow-y-auto">
              <Outlet />
            </div>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*,video/*,audio/*"
              className="hidden"
              onChange={() => {}}
            />
          </UploadDropzone>
        )}
      </div>

      {/* Search overlay (renders on top when open) */}
      <SearchOverlay />
    </>
  )
}
